/**
 * v4.40.0 — Timeline event feed (Review Shield Phase 3).
 *
 * Unified Recent Activity surface. Reads /api/timeline/events which
 * adapts existing collections (review_shield_feedback + broadcasts)
 * into a single event stream. No polling, no new design language,
 * no schema cost.
 *
 * Layout:
 *   • Header with filter pills (All / Feedback / Recovery / Coupons /
 *     Broadcasts / Reviews).
 *   • Newest-first list of cards. Each card = icon · title ·
 *     relative timestamp · location · experience badge.
 *   • Empty-state copy when the active slice has no events.
 *
 * Insight Mode anchors via data-insight-id on the feed container,
 * every filter pill, and every event card. Guidance entries live in
 * backend/operational_base/timeline_v440_seed.py.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    MessageSquare, AlertTriangle, Phone, CheckCircle2, Megaphone,
    Activity, Loader2,
} from 'lucide-react';
import api, { timelineAPI } from '../lib/api';
import { ExperienceBadge } from '../components/shared/ExperienceBadge';
import { isMedicalVertical, scopedOrgVertical } from '../lib/vertical';
import { useAuth } from '../lib/auth';

const FILTERS = ['all', 'feedback', 'recovery', 'coupons', 'broadcasts', 'reviews'];

// kind → icon + accent token (reuses existing palette; no new design).
const KIND_META = {
    feedback_received:    { Icon: MessageSquare, fg: '#4AA3FF' },
    low_rating_submitted: { Icon: AlertTriangle, fg: '#FFB347' },
    contact_requested:    { Icon: Phone,         fg: '#FFC837' },
    feedback_resolved:    { Icon: CheckCircle2,  fg: '#30E38C' },
    broadcast_sent:       { Icon: Megaphone,     fg: '#B06CFF' },
};

const relativeTime = (iso, t) => {
    if (!iso) return '';
    const ms = Date.now() - Date.parse(iso);
    if (Number.isNaN(ms)) return '';
    const mins = Math.floor(ms / 60_000);
    if (mins < 1) return t('timeline.just_now');
    if (mins < 60) return t('timeline.minutes_ago', { n: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('timeline.hours_ago', { n: hrs });
    const days = Math.floor(hrs / 24);
    return t('timeline.days_ago', { n: days });
};

const pickLocale = (obj, lang) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return (obj[lang] && obj[lang].trim()) ? obj[lang] : (obj.en || '');
};

const TimelineCard = ({ event, t, lang, onActivate, clickable, hideExperience }) => {
    const meta = KIND_META[event.kind] || KIND_META.feedback_received;
    const Icon = meta.Icon;
    const title = event.kind === 'broadcast_sent'
        ? pickLocale(event.title, lang) || t('timeline.event.broadcast_sent')
        : t(`timeline.event.${event.kind}`);
    const ratingStars = event.rating ? '★'.repeat(event.rating) + '☆'.repeat(5 - event.rating) : null;

    // v4.40.1 — Click + Enter/Space activate when card has a deep-link target.
    const handleKey = (e) => {
        if (!clickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onActivate();
        }
    };

    return (
        <div
            className={`flex items-start gap-3 p-4 border border-border rounded-xl bg-card transition-colors ${clickable ? 'hover:bg-muted/60 cursor-pointer focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none' : 'hover:bg-muted/40'}`}
            data-testid={`timeline-card-${event.id}`}
            data-insight-id="timeline-card"
            data-event-kind={event.kind}
            data-clickable={clickable ? 'true' : 'false'}
            onClick={clickable ? onActivate : undefined}
            onKeyDown={handleKey}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={clickable ? t('timeline.openSource', { title }) : undefined}
        >
            <div
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${meta.fg}22`, color: meta.fg, border: `1px solid ${meta.fg}55` }}
            >
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{title}</span>
                    {ratingStars && <span className="text-xs text-amber-500 font-mono">{ratingStars}</span>}
                    {event.experience_type && event.category === 'feedback' && !hideExperience && (
                        <ExperienceBadge kind={event.experience_type} size="xs" />
                    )}
                </div>
                {event.snippet && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{event.snippet}</p>
                )}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground mt-1.5">
                    <span>{relativeTime(event.timestamp, t)}</span>
                    {event.location_name && <span className="truncate max-w-[200px]">· {event.location_name}</span>}
                    {event.kind === 'broadcast_sent' && event.priority && (
                        <span className="uppercase tracking-wider font-semibold" style={{ color: meta.fg }}>
                            · {t(`timeline.priority.${event.priority}`)}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

const Timeline = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user, isSuperAdmin } = useAuth();
    const [filter, setFilter] = useState('all');
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    // MEDICAL ONLY — a clinic event never shows the dine-in / take-out /
    // delivery badge. A super admin sees every vertical in one feed, so the
    // medical organizations are resolved per event; a tenant uses its own
    // vertical. No other vertical changes.
    const [medicalOrgIds, setMedicalOrgIds] = useState([]);
    useEffect(() => {
        if (!isSuperAdmin()) return;
        api.get('/organizations')
            .then((r) => setMedicalOrgIds((r.data || [])
                .filter((o) => isMedicalVertical(o?.vertical)).map((o) => o.id)))
            .catch(() => setMedicalOrgIds([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const tenantIsMedical = !isSuperAdmin()
        && isMedicalVertical(scopedOrgVertical({ user }));
    const isMedicalEvent = (event) => tenantIsMedical
        || (!!event?.organization_id && medicalOrgIds.includes(event.organization_id));

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const res = await timelineAPI.list(filter, 200);
                if (!cancelled) setEvents(res.data?.events || []);
            } catch (_e) {
                if (!cancelled) setEvents([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [filter]);

    // v4.40.1 — Deep-link routing. Feedback rows → Results (with feedback_id
    // anchor + experience preselected). Broadcasts → Admin Broadcasts (super
    // admin only — for everyone else the card stays non-clickable).
    const routeForEvent = (event) => {
        if (event.category === 'feedback' && event.feedback_id) {
            const params = new URLSearchParams({ feedback_id: event.feedback_id });
            if (event.experience_type) params.set('experience', event.experience_type);
            return `/review-shield-results?${params.toString()}`;
        }
        if (event.kind === 'broadcast_sent' && event.broadcast_id && isSuperAdmin()) {
            return `/admin/broadcasts?selected=${encodeURIComponent(event.broadcast_id)}`;
        }
        return null;
    };

    return (
        <div className="p-6 max-w-4xl mx-auto" data-testid="timeline-page" data-insight-id="timeline-feed">
            <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #4AA3FF22, #30E38C22)', border: '1px solid #4AA3FF55' }}>
                    <Activity className="w-5 h-5 text-sky-500" />
                </div>
                <div>
                    <h1 className="text-2xl font-semibold" data-testid="timeline-title">{t('timeline.title')}</h1>
                    <p className="text-sm text-muted-foreground">{t('timeline.subtitle')}</p>
                </div>
            </div>

            <div className="flex flex-wrap gap-1.5 mt-5 mb-5" data-testid="timeline-filters">
                {FILTERS.map((f) => {
                    const active = filter === f;
                    return (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-sky-500/15 border-sky-500/50 text-sky-600' : 'border-border text-muted-foreground hover:bg-muted'}`}
                            data-testid={`timeline-filter-${f}`}
                            data-insight-id={`timeline-filter-${f}`}
                            aria-pressed={active}
                        >
                            {t(`timeline.filters.${f}`)}
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <div className="text-center py-16 text-muted-foreground">
                    <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                </div>
            ) : events.length === 0 ? (
                <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-muted-foreground" data-testid="timeline-empty">
                    {t('timeline.empty')}
                </div>
            ) : (
                <div className="space-y-2" data-testid="timeline-list">
                    {events.map((e) => {
                        const target = routeForEvent(e);
                        return (
                            <TimelineCard
                                key={e.id}
                                event={e}
                                t={t}
                                lang={i18n.language}
                                clickable={!!target}
                                hideExperience={isMedicalEvent(e)}
                                onActivate={() => target && navigate(target)}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Timeline;
